import { UploadFile } from 'components/applets/app-upload';

export interface VideoCompressionOptions {
  resolution?: string; // e.g., '1920x1080'
  bitrate?: string; // e.g., '5000k'
  fps?: number; // e.g., 30
  codec?: string; // e.g., 'libx264'
  preset?: string; // e.g., 'medium'
  audioCodec?: string; // e.g., 'aac'
  audioBitrate?: string; // e.g., '128k'
  maxFileSize?: number; // in bytes, e.g., 100 * 1024 * 1024
}

export interface VideoMetadata {
  width?: number;
  height?: number;
  duration?: number; // in seconds
  bitrate?: number;
  size?: number; // in bytes
  fps?: number;
}

const MAX_ORIGINAL_SIZE = 10 * 1024 * 1024 * 1024; // 10,000 MB (10 GB)
const MAX_SIZE_WITHOUT_COMPRESSION = 100 * 1024 * 1024; // 100 MB - Larger videos will be compressed in the backend

/**
 * Checks if a file is a video
 */
export const isVideoFile = (file: UploadFile): boolean => {
  const mimeType = file.mime || file.type || '';
  return mimeType.includes('video') || mimeType.includes('movie');
};

/**
 * Gets basic file information (size)
 * Note: Real compression is done in the backend
 */
export const getVideoFileInfo = async (file: UploadFile): Promise<{ size: number } | null> => {
  try {
    // If we have the original File object, use it directly (more reliable and efficient)
    if (file.originalFile) {
      return {
        size: file.originalFile.size,
      };
    }

    // Otherwise, try to fetch from URI
    const response = await fetch(file.uri);
    const blob = await response.blob();
    return {
      size: blob.size,
    };
  } catch (error) {
    console.error('Error getting file info:', error);
    return null;
  }
};

/**
 * Determines if a video needs compression based on its size
 * Real compression will be performed in the backend
 */
export const needsCompression = async (file: UploadFile): Promise<boolean> => {
  // If it's not a video, it doesn't need compression
  if (!isVideoFile(file)) {
    return false;
  }

  // Get file information
  const fileInfo = await getVideoFileInfo(file);
  if (!fileInfo) {
    // If we can't get info, assume it needs compression for safety
    return true;
  }

  // Check maximum size
  if (fileInfo.size > MAX_SIZE_WITHOUT_COMPRESSION) {
    return true;
  }

  return false;
};

/**
 * Validates if a video can be uploaded
 * Returns an error if the video is too large even for compression
 */
export const validateVideoForUpload = async (
  file: UploadFile,
): Promise<{ valid: boolean; error?: string }> => {
  if (!isVideoFile(file)) {
    return { valid: true };
  }

  const fileInfo = await getVideoFileInfo(file);
  if (!fileInfo) {
    // If we can't get the file size (can happen with very large files),
    // allow it to continue. Size will be validated when the blob is obtained for upload.
    console.warn('[VALIDATE] Could not get video size, will validate during upload');
    return { valid: true };
  }

  // If the file is larger than 10,000 MB, reject it
  if (fileInfo.size > MAX_ORIGINAL_SIZE) {
    return {
      valid: false,
      error: 'Your file exceeds the size limit (100 mb for images, 10,000 mb for video). Please try again',
    };
  }

  return { valid: true };
};

/**
 * Simulated compression function - only validates
 * Real compression is done in the backend after upload
 * 
 * This function exists to maintain compatibility with existing code,
 * but in reality it only validates and returns the original file
 */
export const compressVideo = async (
  file: UploadFile,
  options: Partial<VideoCompressionOptions> = {},
  onProgress?: (progress: number) => void,
): Promise<UploadFile> => {
  // Validate the file
  const validation = await validateVideoForUpload(file);
  if (!validation.valid) {
    throw new Error(validation.error || 'The video is not valid for upload');
  }

  // Simulate "compression" progress (actually only validation)
  if (onProgress) {
    // Simulate fast progress since we're only validating
    onProgress(10);
    await new Promise((resolve) => setTimeout(resolve, 100));
    onProgress(50);
    await new Promise((resolve) => setTimeout(resolve, 100));
    onProgress(100);
  }

  // Return the original file - the backend will compress it
  return file;
};

